import React, { useState, useEffect, useCallback } from 'react';
import { auth } from '../firebase/config';
import { signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import PartnerEditModal from './PartnerEditModal';
import EventEditModal from './EventEditModal';
import AppreciationEditModal from './AppreciationEditModal';
import { HandHeart, Image, DoorOpen, Calendar, Heart, CheckCircle, XCircle } from 'lucide-react';
import seal from "../assets/Background.png";
import { toast } from 'sonner';




const CDN_BASE_URL = "https://storage.googleapis.com/wacofirefightersbenefit";
const FUNCTIONS_BASE_URL = process.env.REACT_APP_FUNCTIONS_URL || "https://us-central1-wacofirefightersbenefit.cloudfunctions.net";

const Console = () => {
    const [partnerImages, setPartnerImages] = useState([]);
    const [aboutImages, setAboutImages] = useState([]);
    const [eventImages, setEventImages] = useState([]);
    const [appreciationImages, setAppreciationImages] = useState([]);
    const [partnersData, setPartnersData] = useState([]);
    const [eventsData, setEventsData] = useState([]);
    const [appreciationData, setAppreciationData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [activeTab, setActiveTab] = useState('events');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingPartner, setEditingPartner] = useState(null);
    const [editingEvent, setEditingEvent] = useState(null);
    const [editingTestimonial, setEditingTestimonial] = useState(null);
    const [isEventModalOpen, setIsEventModalOpen] = useState(false);
    const [isAppreciationModalOpen, setIsAppreciationModalOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const navigate = useNavigate();

    const handleClick = useCallback((icon, text) => {
        if (icon === 'checkCircle') {
            toast.success(text, {
                duration: 5000,
                icon: <CheckCircle className="w-4 h-4 mr-2 text-green-500" />,
                position: 'top-center',
            });
        } else {
            toast.error(text, {
                duration: 5000,
                icon: <XCircle className="w-4 h-4 mr-2 text-red-500" />,
                position: 'top-center',
            });
        }
    }, []);

    const loadImages = useCallback(async () => {
        setLoading(true);

        try {
            const token = await auth.currentUser.getIdToken();

            // Fetch list of images from Cloud Function
            const response = await fetch(`${FUNCTIONS_BASE_URL}/listImages`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Failed to load images');
            }

            const data = await response.json();
            setPartnerImages(data.partners || []);
            setAboutImages(data.about || []);
            setEventImages(data.events || []);
            setAppreciationImages(data.appreciation || []);
        } catch (err) {
            console.error('Error loading images:', err);
            handleClick('xCircle', 'Failed to load images. Make sure Cloud Functions are deployed.');
        } finally {
            setLoading(false);
        }
    }, [handleClick]);

    const loadPartnersData = useCallback(async () => {
        try {
            const response = await fetch(`${CDN_BASE_URL}/data/partners.json?t=${Date.now()}`);
            if (response.ok) {
                const data = await response.json();
                setPartnersData(data);
                console.log('Partners data loaded successfully:', data.length, 'partners');
            } else {
                console.warn(`Failed to load partners data: ${response.status} ${response.statusText}`);
                setPartnersData([]); // Initialize with empty array if file doesn't exist
            }
        } catch (err) {
            console.error('Error loading partners data:', err);
            setPartnersData([]); // Initialize with empty array on error
            handleClick('xCircle', 'Failed to load partners data');
        }
    }, [handleClick]);

    const loadEventsData = useCallback(async () => {
        try {
            const response = await fetch(`${CDN_BASE_URL}/data/events.json?t=${Date.now()}`);
            if (response.ok) {
                const data = await response.json();
                setEventsData(data);
            } else {
                console.warn('Failed to load events data');
                setEventsData([]);
            }
        } catch (err) {
            console.error('Error loading events data:', err);
            setEventsData([]);
            handleClick('xCircle', 'Failed to load events data');
        }
    }, [handleClick]);

    const loadAppreciationData = useCallback(async () => {
        try {
            const response = await fetch(`${CDN_BASE_URL}/data/appreciation.json?t=${Date.now()}`);
            if (response.ok) {
                const data = await response.json();
                // Support both array and single object for backward compatibility
                setAppreciationData(Array.isArray(data) ? data : [data]);
            } else {
                console.warn('Failed to load appreciation data');
                setAppreciationData([]);
            }
        } catch (err) {
            console.error('Error loading appreciation data:', err);
            setAppreciationData([]);
            handleClick('xCircle', 'Failed to load appreciation data');
        }
    }, [handleClick]);

    useEffect(() => {
        // Check if user is authenticated
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (!user) {
                navigate('/console/login');
            } else {
                loadImages();
                loadPartnersData();
                loadEventsData();
                loadAppreciationData();
            }
        });

        return () => unsubscribe();
    }, [navigate, loadAppreciationData, loadEventsData, loadImages, loadPartnersData]);

    const handleLogout = async () => {
        try {
            await signOut(auth);
            navigate('/console/login');
        } catch (err) {
            console.error('Logout error:', err);
            setError('Failed to log out');
        }
    };

    const handleUpload = async (e, imageType) => {
        const files = Array.from(e.target ? e.target.files : e);
        if (files.length === 0) return [];

        setUploading(true);

        const uploadedPaths = [];

        try {
            const token = await auth.currentUser.getIdToken();

            for (const file of files) {
                const formData = new FormData();
                formData.append('image', file);
                formData.append('type', imageType);

                const response = await fetch(`${FUNCTIONS_BASE_URL}/uploadImage`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    },
                    body: formData
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.error || 'Upload failed');
                }

                const data = await response.json();
                if (data.files && data.files.length > 0) {
                    uploadedPaths.push(data.files[0].filepath);
                }
            }

            handleClick('checkCircle', `Successfully uploaded ${files.length} image(s)`);
            loadImages();
            return uploadedPaths;
        } catch (err) {
            console.error('Upload error:', err);
            handleClick('xCircle', err.message || 'Failed to upload image');
            return [];
        } finally {
            setUploading(false);
            if (e.target) {
                e.target.value = ''; // Reset file input
            }
        }
    };

    const handleDelete = async (imagePath, imageType) => {
        if (!window.confirm('Are you sure you want to delete this image?')) {
            return;
        }

        try {
            const token = await auth.currentUser.getIdToken();

            const response = await fetch(`${FUNCTIONS_BASE_URL}/deleteImage`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ imagePath, type: imageType })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Delete failed');
            }

            handleClick('checkCircle', 'Image deleted successfully');
            loadImages();
        } catch (err) {
            console.error('Delete error:', err);
            handleClick('xCircle', 'Failed to delete image');
        }
    };

    const savePartnersData = async (newPartnersData) => {
        try {
            const token = await auth.currentUser.getIdToken();

            const response = await fetch(`${FUNCTIONS_BASE_URL}/updatePartnersData`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ partners: newPartnersData })
            });

            if (!response.ok) {
                throw new Error('Failed to save partners data');
            }

            setPartnersData(newPartnersData);
            handleClick('checkCircle', 'Partners data saved successfully');
            setIsModalOpen(false);
            setEditingPartner(null);
        } catch (err) {
            console.error('Save error:', err);
            handleClick('xCircle', 'Failed to save partners data');
        }
    };

    const handleSavePartner = (partner) => {
        let newData;
        if (editingPartner) {
            // Edit existing
            newData = partnersData.map(p => p.id === partner.id ? partner : p);
        } else {
            // Add new
            const newPartner = {
                ...partner,
                id: Date.now().toString()
            };
            newData = [...partnersData, newPartner];
        }
        savePartnersData(newData);
        // handleClick('checkCircle', 'Partner saved successfully');
    };

    const handleDeletePartner = (partnerId) => {
        if (!window.confirm('Are you sure you want to remove this partner from the list?')) return;
        const newData = partnersData.filter(p => p.id !== partnerId);
        savePartnersData(newData);
        handleClick('checkCircle', 'Partner deleted successfully');
    };

    const saveEventsData = async (newEventsData) => {
        try {
            const token = await auth.currentUser.getIdToken();

            const response = await fetch(`${FUNCTIONS_BASE_URL}/updateEventsData`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ events: newEventsData })
            });

            if (!response.ok) {
                throw new Error('Failed to save events data');
            }

            setEventsData(newEventsData);
            handleClick('checkCircle', 'Events data saved successfully');
            setIsEventModalOpen(false);
            setEditingEvent(null);
        } catch (err) {
            console.error('Save error:', err);
            handleClick('xCircle', 'Failed to save events data');
        }
    };

    const handleSaveEvent = (event) => {
        let newData;
        if (editingEvent) {
            // Edit existing
            newData = eventsData.map(e => e.id === event.id ? event : e);
        } else {
            // Add new
            const newEvent = {
                ...event,
                id: Date.now().toString()
            };
            newData = [...eventsData, newEvent];
        }
        saveEventsData(newData);
        // handleClick('checkCircle', 'Event saved successfully');
    };

    const handleDeleteEvent = (eventId) => {
        if (!window.confirm('Are you sure you want to delete this event?')) return;
        const newData = eventsData.filter(e => e.id !== eventId);
        saveEventsData(newData);
        handleClick('checkCircle', 'Event deleted successfully');
    };

    const saveAppreciationData = async (newAppreciationData) => {
        setError('');
        setSuccess('');

        try {
            const token = await auth.currentUser.getIdToken();

            const response = await fetch(`${FUNCTIONS_BASE_URL}/updateAppreciationData`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ appreciation: newAppreciationData })
            });

            if (!response.ok) {
                throw new Error('Failed to save appreciation data');
            }

            setAppreciationData(newAppreciationData);
            handleClick('checkCircle', 'Appreciation data saved successfully');
            setIsAppreciationModalOpen(false);
            setEditingTestimonial(null);
        } catch (err) {
            console.error('Save error:', err);
            handleClick('xCircle', 'Failed to save appreciation data');
        }
    };

    const handleSaveTestimonial = (testimonial) => {
        let newData;
        if (editingTestimonial) {
            // Edit existing
            newData = appreciationData.map(t => t.id === testimonial.id ? testimonial : t);
        } else {
            // Add new
            const newTestimonial = {
                ...testimonial,
                id: Date.now().toString()
            };
            newData = [...appreciationData, newTestimonial];
        }
        saveAppreciationData(newData);
    };

    const handleDeleteTestimonial = (testimonialId) => {
        if (!window.confirm('Are you sure you want to delete this testimonial?')) return;
        const newData = appreciationData.filter(t => t.id !== testimonialId);
        saveAppreciationData(newData);
    };

    const renderImageGrid = (images, imageType) => {
        if (loading) {
            return <div className="text-center py-[60px] px-5 text-[#666] text-base">Loading images...</div>;
        }

        if (images.length === 0) {
            return <div className="text-center py-[60px] px-5 text-[#999] text-base">No images found</div>;
        }

        return (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] md:grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-3 md:gap-5">
                {images.map((imageName) => (
                    <div key={imageName} className="bg-white border-2 border-[#e0e0e0] rounded-xl overflow-hidden transition-all duration-200">
                        <img
                            src={`${CDN_BASE_URL}/${imageName}`}
                            alt={imageName}
                            className="w-full h-[180px] object-cover block"
                            onError={(e) => {
                                e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EError%3C/text%3E%3C/svg%3E';
                            }}
                        />
                        <div className="p-3 flex flex-col gap-2">
                            <span className="text-[13px] text-[#333] break-all font-medium">{imageName.split('/').pop()}</span>
                            <button
                                onClick={() => handleDelete(imageName, imageType)}
                                className="px-4 py-2 bg-[#dc3545] text-white border-none rounded-full text-[13px] font-semibold cursor-pointer transition-all duration-200 hover:bg-[#c82333]"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    // Filter partners based on search query
    const filterPartners = (partners) => {
        if (!searchQuery.trim()) return partners;

        const query = searchQuery.toLowerCase();
        return partners.filter(partner =>
            partner.name?.toLowerCase().includes(query) ||
            partner.website?.toLowerCase().includes(query)
        );
    };

    return (
        <div className="min-h-screen">
            {/* Fixed Header */}
            <div className="fixed top-0 left-0 right-0 z-50 bg-white">
                <div className="flex flex-col md:flex-row justify-between items-center px-5 py-3 gap-4">
                    <div className="flex flex-row md:flex-row gap-2 items-center">
                        <img src={seal} alt="Seal" className="w-8 h-8" />
                        <h1 className="text-[28px] font-bold text-[#333]">Admin Console</h1>
                        < button
                            className={`rounded-full px-3 py-1 text-[14px] flex items-center ${activeTab === 'events' ? 'bg-black text-white border-transparent' : 'bg-gray-100'}`}
                            onClick={() => setActiveTab('events')}
                        >
                            <Calendar className="w-4 h-4 mr-2" /> Events
                        </button>
                        <button
                            className={`rounded-full px-3 py-1 text-[14px] flex items-center ${activeTab === 'about' ? 'bg-black text-white border-transparent' : 'bg-gray-100'}`}
                            onClick={() => setActiveTab('about')}
                        >
                            <Image className="w-4 h-4 mr-2" /> Gallery
                        </button>
                        <button
                            className={`rounded-full px-3 py-1 items-center text-[14px] flex items-center ${activeTab === 'partners' ? 'bg-black text-white border-transparent' : 'bg-gray-100'}`}
                            onClick={() => setActiveTab('partners')}
                        >
                            <HandHeart className="w-4 h-4 mr-2" /> Partners
                        </button>

                        <button
                            className={`rounded-full px-3 py-1 text-[14px] flex items-center ${activeTab === 'appreciation' ? 'bg-black text-white border-transparent' : 'bg-gray-100'}`}
                            onClick={() => setActiveTab('appreciation')}
                        >
                            <Heart className="w-4 h-4 mr-2" /> Appreciation
                        </button>

                    </div>
                    {/* Tab Buttons */}
                    <div className="flex gap-3 items-center">


                        {/* Logout Button */}
                        <button onClick={handleLogout} className="rounded-full px-3 py-1 bg-gray-100 text-[14px] flex items-center">
                            <DoorOpen className="w-4 h-4 mr-2" /> Logout
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Content Area - with top padding to account for fixed header */}
            <div className="pt-[120px] md:pt-[100px] p-5">
                {error && (
                    <div className="px-[18px] py-[14px] rounded-lg mb-5 border-l-4 text-sm" style={{ backgroundColor: '#fee', borderColor: '#c33' }}>
                        {error}
                    </div>
                )}

                {success && (
                    <div className="px-[18px] py-[14px] rounded-lg mb-5 border-l-4 text-sm" style={{ backgroundColor: '#efe', borderColor: '#3c3' }}>
                        {success}
                    </div>
                )}

                <div className="bg-white rounded-xl  p-6">
                    {activeTab === 'partners' && (
                        <div className="w-full">
                            <div className="mb-6 flex justify-between items-center">
                                <h2 className="text-xl font-semibold"> Preferred Partners</h2>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        placeholder="Search Partners"
                                        className="border border-gray-200 rounded-full px-3 py-1"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                    <button
                                        onClick={() => {
                                            setEditingPartner(null);
                                            setIsModalOpen(true);
                                        }}
                                        className="px-3 py-0 bg-black text-white rounded-full text-sm font-semibold hover:bg-gray-800 transition-colors"
                                    >
                                        + Add New Partner
                                    </button>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {filterPartners(partnersData.filter(partner => partner.preferred))
                                    .map((partner) => (
                                        <div key={partner.id} className="border border-gray-200 rounded-lg p-4 flex items-start gap-4">
                                            <img
                                                src={`${CDN_BASE_URL}/${partner.image}`}
                                                alt={partner.name}
                                                className="w-20 h-20 object-contain rounded bg-gray-50"
                                            />
                                            <div className="flex-1 min-w-0 text-left">
                                                <h3 className="font-semibold text-gray-900 truncate">{partner.name}</h3>
                                                <a href={partner.website} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline truncate block">
                                                    {partner.website || 'No website'}
                                                </a>
                                                <div className="mt-3 flex gap-2">
                                                    <button
                                                        onClick={() => {
                                                            setEditingPartner(partner);
                                                            setIsModalOpen(true);
                                                        }}
                                                        className="px-3 py-1 bg-gray-100 text-gray-700 text-xs font-semibold rounded-full hover:bg-gray-200"
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeletePartner(partner.id)}
                                                        className="px-3 py-1 bg-red-50 text-red-600 text-xs font-semibold rounded-full hover:bg-red-100"
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                            </div>
                            <h2 className="text-xl font-semibold py-5 w-full text-left">All Partners</h2>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {filterPartners(partnersData.filter(partner => !partner.preferred))
                                    .map((partner) => (
                                        <div key={partner.id} className="border border-gray-200 rounded-lg p-4 flex items-start gap-4">
                                            <img
                                                src={`${CDN_BASE_URL}/${partner.image}`}
                                                alt={partner.name}
                                                className="w-20 h-20 object-contain rounded bg-gray-50"
                                            />
                                            <div className="flex-1 min-w-0 text-left">
                                                <h3 className="font-semibold text-gray-900 truncate">{partner.name}</h3>
                                                <a href={partner.website} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline truncate block">
                                                    {partner.website || 'No website'}
                                                </a>
                                                <div className="mt-3 flex gap-2">
                                                    <button
                                                        onClick={() => {
                                                            setEditingPartner(partner);
                                                            setIsModalOpen(true);
                                                        }}
                                                        className="px-3 py-1 bg-gray-100 text-gray-700 text-xs font-semibold rounded-full hover:bg-gray-200"
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeletePartner(partner.id)}
                                                        className="px-3 py-1 bg-red-50 text-red-600 text-xs font-semibold rounded-full hover:bg-red-100"
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    )}

                    {activeTab === 'about' && (
                        <div className="w-full">
                            <div className="mb-8 p-6 bg-[#f9f9f9] border-2 dashed border-[#d0d0d0] rounded-xl text-center">
                                <label className="px-3 py-2 bg-black text-white rounded-full text-sm font-semibold hover:bg-gray-800 transition-colors cursor-pointer">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        onChange={(e) => handleUpload(Array.from(e.target.files), 'about')}
                                        disabled={uploading}
                                        style={{ display: 'none' }}
                                    />
                                    {uploading ? 'Uploading...' : '+ Upload Image(s)'}
                                </label>
                                <p className="mt-3 text-[13px] text-[#666]">
                                    Accepted formats: .jpg, .png, .webp
                                </p>
                            </div>
                            {renderImageGrid(aboutImages, 'about')}
                        </div>
                    )}

                    {activeTab === 'events' && (
                        <div className="w-full">
                            <div className="mb-6 flex justify-between items-center">
                                <h2 className="text-xl font-semibold">Manage Events</h2>
                                <button
                                    onClick={() => {
                                        setEditingEvent(null);
                                        setIsEventModalOpen(true);
                                    }}
                                    className="px-3 py-2 bg-black text-white rounded-full text-sm font-semibold hover:bg-gray-800 transition-colors"
                                >
                                    + Add New Event
                                </button>
                            </div>
                            <div className="space-y-4 w-[600px]">
                                {eventsData
                                    .sort((a, b) => new Date(b.date) - new Date(a.date))
                                    .map((event) => (
                                        <div key={event.id} className="border border-gray-200 rounded-lg p-4 w-[600px] text-left">
                                            <div className="flex justify-between items-start">
                                                <div className="flex-1 flex">
                                                    {event.images && event.images.length > 0 && (
                                                        <img src={`${CDN_BASE_URL}/${event.images[0]}`} alt={event.title} className="h-64 object-cover rounded-md shadow-lg border-[1px] border-gray-200" />
                                                    )}
                                                    <div className="ml-4 flex flex-col justify-between">
                                                        <h3 className="font-semibold text-lg text-gray-900">{event.title}</h3>
                                                        <p className="text-sm text-gray-600 mt-1">Date: {event.displayDate}</p>
                                                        <p className="text-sm text-gray-600">Location: {event.location}</p>
                                                        <p className="text-sm text-gray-600">Address: {event.address}</p>
                                                        {event.images && event.images.length > 0 && (
                                                            <p className="text-sm text-gray-500 mt-2">{event.images.length} image(s) attached</p>
                                                        )}
                                                        {event.photosUrl && (
                                                            <a href={event.photosUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 mt-1">Photos link</a>
                                                        )}
                                                        <div className="flex gap-2 mt-auto">
                                                            <button
                                                                onClick={() => {
                                                                    setEditingEvent(event);
                                                                    setIsEventModalOpen(true);
                                                                }}
                                                                className="px-3 py-1 bg-gray-100 text-gray-700 text-xs font-semibold rounded-full hover:bg-gray-200"
                                                            >
                                                                Edit
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteEvent(event.id)}
                                                                className="px-3 py-1 bg-red-50 text-red-600 text-xs font-semibold rounded-full hover:bg-red-100"
                                                            >
                                                                Delete
                                                            </button>
                                                        </div>
                                                    </div>

                                                </div>

                                            </div>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    )}

                    {activeTab === 'appreciation' && (
                        <div className="w-full">
                            <div className="mb-6 flex justify-between items-center">
                                <h2 className="text-xl font-semibold">Manage Appreciation Testimonials</h2>
                                <button
                                    onClick={() => {
                                        setEditingTestimonial(null);
                                        setIsAppreciationModalOpen(true);
                                    }}
                                    className="px-3 py-2 bg-black text-white rounded-full text-sm font-semibold hover:bg-gray-800 transition-colors"
                                >
                                    + Add New Testimonial
                                </button>
                            </div>
                            <div className="space-y-4">
                                {appreciationData.map((testimonial) => (
                                    <div key={testimonial.id} className="border border-gray-200 rounded-lg p-4">
                                        <div className="flex justify-between items-start gap-4">
                                            <div className="flex-1">
                                                <h3 className="font-semibold text-lg text-gray-900">{testimonial.author}</h3>
                                                <div
                                                    className="text-sm text-gray-600 mt-2 line-clamp-3"
                                                    dangerouslySetInnerHTML={{ __html: testimonial.text }}
                                                />
                                                {testimonial.imageUrl && (
                                                    <p className="text-sm text-gray-500 mt-2">Image: {testimonial.imageUrl}</p>
                                                )}
                                            </div>
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => {
                                                        setEditingTestimonial(testimonial);
                                                        setIsAppreciationModalOpen(true);
                                                    }}
                                                    className="px-3 py-1 bg-gray-100 text-gray-700 text-xs font-semibold rounded-full hover:bg-gray-200"
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteTestimonial(testimonial.id)}
                                                    className="px-3 py-1 bg-red-50 text-red-600 text-xs font-semibold rounded-full hover:bg-red-100"
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <PartnerEditModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSave={handleSavePartner}
                    partner={editingPartner}
                    availableImages={partnerImages}
                    onUpload={(files) => handleUpload(files, 'partners')}
                />
                <EventEditModal
                    isOpen={isEventModalOpen}
                    onClose={() => setIsEventModalOpen(false)}
                    onSave={handleSaveEvent}
                    event={editingEvent}
                    availableImages={eventImages}
                    onUpload={(files) => handleUpload(files, 'events')}
                />
                <AppreciationEditModal
                    isOpen={isAppreciationModalOpen}
                    onClose={() => setIsAppreciationModalOpen(false)}
                    onSave={handleSaveTestimonial}
                    testimonial={editingTestimonial}
                    availableImages={appreciationImages}
                    onUpload={(files) => handleUpload(files, 'appreciation')}
                />
            </div>
        </div>
    );
};

export default Console;
